import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  FolderKanban,
  Wrench,
  Plus,
  Trash2,
  Loader2,
  TrendingUp,
  AlertCircle,
  Layers,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MONTHS = (() => {
  const out = [{ value: "all", label: "All time" }];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value: v, label: d.toLocaleString("en", { month: "short", year: "numeric" }) });
  }
  return out;
})();

function monthRange(m) {
  if (m === "all") return {};
  const [y, mo] = m.split("-").map(Number);
  const last = new Date(y, mo, 0).getDate();
  return { from: `${m}-01`, to: `${m}-${String(last).padStart(2, "0")}` };
}

const money = (n) => formatMoney(Math.abs(Number(n || 0)), "INR");

function NetCell({ v }) {
  const n = Number(v || 0);
  const cls = n > 0 ? "text-success" : n < 0 ? "text-danger" : "text-muted-foreground";
  return (
    <span className={`font-mono tabular-nums font-medium ${cls}`}>
      {n < 0 ? "−" : ""}
      {money(n)}
    </span>
  );
}

function ReportTable({ title, icon: Icon, rows, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 pl-1">Nothing tagged here yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-medium px-3 py-2">Name</th>
                <th className="text-right font-medium px-3 py-2">Revenue</th>
                <th className="text-right font-medium px-3 py-2">Cost</th>
                <th className="text-right font-medium px-3 py-2">Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tag_id} className="border-b border-border/60 last:border-0" data-testid="report-row">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">{money(r.revenue)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">{money(r.cost)}</td>
                  <td className="px-3 py-2 text-right"><NetCell v={r.net} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TagColumn({ type, title, icon: Icon, tags, onAdd, onDelete, busy }) {
  const [name, setName] = useState("");
  const list = tags.filter((t) => t.type === type);
  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    const ok = await onAdd(n, type);
    if (ok !== false) setName("");
  };
  return (
    <Card className="p-4" data-testid={`tag-col-${type}`}>
      <p className="font-medium flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${type === "project" ? "text-brand" : "text-brand2"}`} /> {title}
        <Badge variant="outline" className="ml-auto font-normal">{list.length}</Badge>
      </p>
      <div className="flex items-center gap-2 mb-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={type === "project" ? "e.g. Product X" : "e.g. Research"}
          className="h-9"
          data-testid={`tag-input-${type}`}
        />
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1 shrink-0" data-testid={`tag-add-${type}`}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <div className="space-y-1">
        {list.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
        {list.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded border border-border bg-muted/20 px-2.5 py-1.5 text-sm" data-testid="tag-row">
            <span className="truncate">{t.name}</span>
            <button
              onClick={() => onDelete(t)}
              className="text-muted-foreground hover:text-danger p-0.5 shrink-0"
              data-testid={`tag-delete-${t.id}`}
              aria-label="Delete tag"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function CostCenters() {
  const [tags, setTags] = useState([]);
  const [report, setReport] = useState(null);
  const [untagged, setUntagged] = useState(null);
  const [month, setMonth] = useState("all");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadTags = useCallback(async () => {
    const { data } = await api.get("/cost-tags");
    setTags(data.tags || []);
  }, []);

  const loadReport = useCallback(async (m) => {
    const { from, to } = monthRange(m);
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const [{ data: rep }, { data: ut }] = await Promise.all([
      api.get("/cost-tags/report", { params }),
      api.get("/cost-tags/untagged"),
    ]);
    setReport(rep);
    setUntagged(ut);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadTags(), loadReport(month)])
      .catch(() => toast.error("Could not load cost tags"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMonth = async (m) => {
    setMonth(m);
    try {
      await loadReport(m);
    } catch {
      toast.error("Could not refresh report");
    }
  };

  const addTag = async (name, type) => {
    setBusy(true);
    try {
      await api.post("/cost-tags", { name, type });
      await Promise.all([loadTags(), loadReport(month)]);
      toast.success(`Added ${type === "project" ? "project" : "cost centre"} “${name}”`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add tag");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const delTag = async (t) => {
    if (!window.confirm(`Delete “${t.name}”? Any entries tagged to it will become untagged.`)) return;
    try {
      const { data } = await api.delete(`/cost-tags/${t.id}`);
      await Promise.all([loadTags(), loadReport(month)]);
      toast.success(data.unlinked ? `Deleted “${t.name}” · ${data.unlinked} entr${data.unlinked === 1 ? "y" : "ies"} untagged` : `Deleted “${t.name}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete tag");
    }
  };

  const untaggedTotal = untagged
    ? (untagged.expenses?.amount || 0) + (untagged.income?.amount || 0) + (untagged.payables?.amount || 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="cost-centers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center mt-0.5">
            <Layers className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Projects &amp; Cost Centres</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Tag every spend &amp; revenue to a project or cost centre to see what each actually earns.
            </p>
          </div>
        </div>
        <Select value={month} onValueChange={onMonth}>
          <SelectTrigger className="w-44" data-testid="report-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {MONTHS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Contribution report */}
          <Card className="p-5 space-y-5" data-testid="contribution-report">
            <div className="flex items-center justify-between">
              <p className="font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-brand" /> Contribution
              </p>
              {report && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Revenue <span className="font-mono text-foreground">{money(report.totals.revenue)}</span></span>
                  <span className="text-muted-foreground">Cost <span className="font-mono text-foreground">{money(report.totals.cost)}</span></span>
                  <span className="text-muted-foreground">Net <NetCell v={report.totals.net} /></span>
                </div>
              )}
            </div>
            {report && (
              <div className="grid md:grid-cols-2 gap-5">
                <ReportTable title="Projects (revenue-generating)" icon={FolderKanban} rows={report.projects} testid="report-projects" />
                <ReportTable title="Cost Centres (overhead)" icon={Wrench} rows={report.cost_centres} testid="report-cost-centres" />
              </div>
            )}
            {report && (report.untagged.revenue > 0 || report.untagged.cost > 0) && (
              <div className="flex items-center justify-between rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm" data-testid="report-untagged">
                <span className="flex items-center gap-1.5 text-warning font-medium">
                  <AlertCircle className="h-4 w-4" /> Untagged
                </span>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-muted-foreground">Revenue <span className="font-mono">{money(report.untagged.revenue)}</span></span>
                  <span className="text-muted-foreground">Cost <span className="font-mono">{money(report.untagged.cost)}</span></span>
                </div>
              </div>
            )}
          </Card>

          {/* Untagged cleanup */}
          {untagged && untaggedTotal > 0 && (
            <Card className="p-4" data-testid="untagged-bucket">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{money(untaggedTotal)}</span> is still untagged across
                {" "}
                {["expenses", "income", "payables"]
                  .filter((k) => untagged[k]?.count)
                  .map((k) => `${untagged[k].count} ${k}`)
                  .join(", ")}
                . Open a ledger, hit the tag icon on a row, and split it.
              </p>
            </Card>
          )}

          {/* Tag master */}
          <div className="grid md:grid-cols-2 gap-4">
            <TagColumn type="project" title="Projects" icon={FolderKanban} tags={tags} onAdd={addTag} onDelete={delTag} busy={busy} />
            <TagColumn type="cost_centre" title="Cost Centres" icon={Wrench} tags={tags} onAdd={addTag} onDelete={delTag} busy={busy} />
          </div>
        </>
      )}
    </div>
  );
}
